import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiClient } from "@/App";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  Zap,
  Dumbbell,
  Clock,
  Target,
  Star,
  Play,
  ChevronRight,
  Sparkles,
  MapPin,
  MessageSquare,
} from "lucide-react";

const BeTrain = () => {
  const [trainers, setTrainers] = useState([]);
  const [workoutPlans, setWorkoutPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState("build muscle");
  const [selectedDifficulty, setSelectedDifficulty] = useState("intermediate");
  const [selectedDuration, setSelectedDuration] = useState("4");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [trainersRes, plansRes] = await Promise.all([
        apiClient.get("/trainers"),
        apiClient.get("/train/plans"),
      ]);
      setTrainers(trainersRes.data);
      setWorkoutPlans(plansRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateWorkoutPlan = async () => {
    setGenerating(true);
    try {
      const response = await apiClient.post("/train/generate-plan", {
        goal: selectedGoal,
        difficulty: selectedDifficulty,
        duration_weeks: parseInt(selectedDuration),
      });
      toast.success(`Workout plan created! +${response.data.points_earned || 50} points`);
      setWorkoutPlans([response.data, ...workoutPlans]);
    } catch (error) {
      toast.error("Failed to generate workout plan");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="be-train-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl">BE Train</h1>
          </div>
          <p className="text-muted-foreground">
            AI-powered coaching and personal training for your fitness journey.
          </p>
        </motion.div>

        <Tabs defaultValue="ai-coach" className="space-y-8">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="ai-coach" data-testid="ai-coach-tab">AI Coach</TabsTrigger>
            <TabsTrigger value="my-plans" data-testid="my-plans-tab">My Plans</TabsTrigger>
            <TabsTrigger value="trainers" data-testid="trainers-tab">Trainers</TabsTrigger>
          </TabsList>

          {/* AI Coach Tab */}
          <TabsContent value="ai-coach">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Plan Generator */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Card className="bg-gradient-to-br from-blue-500/10 to-purple-500/10">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <CardTitle className="font-heading">AI Workout Generator</CardTitle>
                        <CardDescription>Get a personalized plan in seconds</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Fitness Goal</label>
                      <Select value={selectedGoal} onValueChange={setSelectedGoal}>
                        <SelectTrigger data-testid="goal-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="build muscle">Build Muscle</SelectItem>
                          <SelectItem value="lose weight">Lose Weight</SelectItem>
                          <SelectItem value="increase endurance">Increase Endurance</SelectItem>
                          <SelectItem value="improve flexibility">Improve Flexibility</SelectItem>
                          <SelectItem value="general fitness">General Fitness</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Difficulty Level</label>
                      <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
                        <SelectTrigger data-testid="difficulty-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Duration</label>
                      <Select value={selectedDuration} onValueChange={setSelectedDuration}>
                        <SelectTrigger data-testid="duration-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="2">2 Weeks</SelectItem>
                          <SelectItem value="4">4 Weeks</SelectItem>
                          <SelectItem value="8">8 Weeks</SelectItem>
                          <SelectItem value="12">12 Weeks</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      className="w-full bg-blue-500 hover:bg-blue-600"
                      size="lg"
                      onClick={generateWorkoutPlan}
                      disabled={generating}
                      data-testid="generate-plan-btn"
                    >
                      {generating ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5 mr-2" />
                          Generate My Plan
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Latest Plan Preview */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                {workoutPlans.length > 0 ? (
                  <Card>
                    <CardHeader>
                      <Badge className="w-fit mb-2 bg-blue-500/10 text-blue-600 border-blue-500/20">
                        Latest Plan
                      </Badge>
                      <CardTitle className="font-heading">{workoutPlans[0].title}</CardTitle>
                      <CardDescription>{workoutPlans[0].description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4 mb-6 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {workoutPlans[0].duration_weeks} weeks
                        </span>
                        <span className="flex items-center gap-1">
                          <Target className="w-4 h-4" />
                          {workoutPlans[0].difficulty}
                        </span>
                      </div>

                      <div className="space-y-4">
                        {workoutPlans[0].exercises?.slice(0, 3).map((day, index) => (
                          <div key={index} className="p-4 bg-muted/50 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium">{day.day}</h4>
                              <Badge variant="outline">{day.focus}</Badge>
                            </div>
                            <div className="space-y-1">
                              {day.exercises?.slice(0, 3).map((ex, i) => (
                                <div key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                                  <Play className="w-3 h-3" />
                                  {ex.name} - {ex.sets}x{ex.reps}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="h-full flex items-center justify-center">
                    <CardContent className="text-center py-12">
                      <Dumbbell className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="font-heading font-semibold text-lg mb-2">No Plans Yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Generate your first AI-powered workout plan
                      </p>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            </div>
          </TabsContent>

          {/* My Plans Tab */}
          <TabsContent value="my-plans">
            {workoutPlans.length === 0 ? (
              <Card>
                <CardContent className="text-center py-16">
                  <Dumbbell className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-heading font-semibold text-lg mb-2">No Workout Plans</h3>
                  <p className="text-muted-foreground mb-4">
                    Generate your first workout plan with AI Coach
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {workoutPlans.map((plan, index) => (
                  <motion.div
                    key={plan.plan_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full" data-testid={`plan-card-${plan.plan_id}`}>
                      <CardHeader>
                        <div className="flex items-center justify-between mb-2">
                          <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                            {plan.goal}
                          </Badge>
                          <Badge variant="outline">{plan.difficulty}</Badge>
                        </div>
                        <CardTitle className="font-heading text-lg">{plan.title}</CardTitle>
                        <CardDescription className="line-clamp-2">
                          {plan.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {plan.duration_weeks} weeks
                          </span>
                          <span className="flex items-center gap-1">
                            <Dumbbell className="w-4 h-4" />
                            {plan.exercises?.length || 0} days
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Trainers Tab */}
          <TabsContent value="trainers">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trainers.map((trainer, index) => (
                <motion.div
                  key={trainer.trainer_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="trainer-card h-full" data-testid={`trainer-${trainer.trainer_id}`}>
                    <CardContent className="p-6">
                      <div className="flex gap-4 mb-4">
                        <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0">
                          <img
                            src={trainer.image_url}
                            alt={trainer.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold">{trainer.name}</h3>
                          <Badge variant="outline" className="mt-1">
                            {trainer.specialty}
                          </Badge>
                          <div className="flex items-center gap-1 mt-2">
                            <Star className="w-4 h-4 text-accent fill-accent" />
                            <span className="font-medium">{trainer.rating}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {trainer.bio}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                        <MapPin className="w-4 h-4" />
                        {trainer.location}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-heading font-bold text-lg">
                          ${trainer.price_per_session}/session
                        </span>
                        <Button size="sm" data-testid={`book-trainer-${trainer.trainer_id}`}>
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Book
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default BeTrain;
